import { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  PermissionEnum,
  Popup,
  Tabs,
  SidePanelLoader,
  ResponseError,
  responseErrorsResolver,
  ChangeLogs,
  PermissionDenied,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  AutoExchangeFormValues,
  Period,
  PrimaryAccount,
  Schedule,
  WeekDays,
} from './types';
import AutoSettingsForm from '../../components/auto-settings/form';
import {
  useLazyGetExchangeAutoSettingsItemQuery,
  useChangeSettingsStatusMutation,
  useUpdateExchangeAutoSettingsPairMutation,
  useLazyGetRuleChangeLogsQuery,
  useLazyGetAutoExchangeAccountsCategoriesQuery,
} from './store/services';
import { clearAutoSettingsDetailsState } from './store/slices/sidepanel';
import useGetAccountsAutocompleteList from './hooks/use-get-accounts-list';

import ROUTES from '../../routes';

import cn from './auto-settings.module.css';

const AutoSettingsItem = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [schedule, setSchedule] = useState<Schedule[]>([
    {
      weekday: WeekDays.MONDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.TUESDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.WEDNESDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.THURSDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.FRIDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.SATURDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.SUNDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
  ]);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'log'>('details');
  const [weekdaysWithErrors, setWeekdaysWithErrors] = useState<WeekDays[]>([]);
  const [globalError, setGlobalError] = useState('');
  const [defaultSchedule, setDefaultSchedule] = useState<Schedule[]>([]);
  const [selectedPrimaryAccounts, setSelectedPrimaryAccounts] = useState<
    string[]
  >([]);
  const [primaryAccountsList, setPrimaryAccountsList] = useState<
    PrimaryAccount[]
  >([]);

  const {
    exchangeDictionaries: { activePermissions, currencies, rateSource },
    autoSettings: {
      details: {
        autoSettingsDetails,
        changeLogs,
        changeLogsPagination,
        accountCategories,
      },
    },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const { currency, settingId } = useParams();

  const {
    control,
    reset,
    formState: { errors, isDirty },
    watch,
    setValue,
    handleSubmit,
    setError,
  } = useForm<AutoExchangeFormValues>();

  const formIsDirty = isDirty || !isEqual(schedule, defaultSchedule);

  const currencyFrom = watch('exchangeCurrencyFromId');
  const currencyTo = watch('exchangeCurrencyToId');
  const status = watch('active');
  const exchangeType = watch('exchangeType');
  const accountsType = watch('accountsType');
  const createChildrenTransactions = watch('createChildrenTransactions');
  const fields = watch();

  const [getDetails, { isLoading, isFetching }] =
    useLazyGetExchangeAutoSettingsItemQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetRuleChangeLogsQuery();

  const [changeStatus, { isLoading: changeStatusInProgress, isError }] =
    useChangeSettingsStatusMutation();
  const [update, { isLoading: requestInProgress }] =
    useUpdateExchangeAutoSettingsPairMutation();
  const [getAccountsCategories, { isLoading: accountsCategoriesLoading }] =
    useLazyGetAutoExchangeAccountsCategoriesQuery();

  const navigate = useNavigate();
  const { search } = useLocation();

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=EXCHANGE_AUTO_RULE&entityId=${settingId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [settingId],
  );

  const {
    autocompleteList: fromAccounts,
    onSearch: onSearchFromAccountsList,
    isLoading: fromAccountsListIsLoading,
    nextPageIsLoading: fromAccountsNextPageIsLoading,
    onScroll: onScrollFromAccounts,
    pagination: fromAccountsPagination,
    getAccountBytId: getFromAccountById,
  } = useGetAccountsAutocompleteList();

  const {
    autocompleteList: toAccounts,
    onSearch: onSearchToAccountsList,
    isLoading: toAccountsListIsLoading,
    nextPageIsLoading: toAccountsNextPageIsLoading,
    onScroll: onScrollToAccounts,
    pagination: toAccountsPagination,
    getAccountBytId: getToAccountById,
  } = useGetAccountsAutocompleteList();

  const onSearchAutocomplete = (name: string, searchValue: string) => {
    if (name === 'fromAccount') {
      onSearchFromAccountsList(searchValue);
    } else if (name === 'toAccount') {
      onSearchToAccountsList(searchValue);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    let updateIsAvailable = true;

    setGlobalError('');

    const periods: Period[] = [];
    const activeWeekDays: WeekDays[] = [];
    const weekdaysErrors: WeekDays[] = [];

    const body: AutoExchangeFormValues = {
      rateSourceLifetimeSeconds: Number(values.rateSourceLifetimeSeconds),
      maxAmountCurrencyId: values.maxAmountCurrencyId,
      primaryAccountIds: values.primaryAccountIds,
      createChildrenTransactions: values.createChildrenTransactions,
      fromAccountId: values.fromAccountId || null,
      toAccountId: values.toAccountId || null,
      rateSourceId: values.rateSourceId || null,
      accountsType: values.accountsType,
    };

    if (values.maxAmountPerOperation) {
      body.maxAmountPerOperation = Number(values.maxAmountPerOperation);
    }

    if (values.minAmountPerOperation) {
      body.minAmountPerOperation = Number(values.minAmountPerOperation);
    }

    if (values.maxAmountForPeriod) {
      body.maxAmountForPeriod = Number(values.maxAmountForPeriod);
    }

    if (values.maxAmountPeriodInHours) {
      body.maxAmountPeriodInHours = Number(values.maxAmountPeriodInHours) * 24;
    }

    if (values.targetStatus) {
      body.targetStatus = values.targetStatus;
    }

    if (!isEqual(schedule, defaultSchedule)) {
      schedule?.forEach((item) => {
        if (item.active) {
          activeWeekDays.push(item.weekday);

          const ranges = item.timerange?.filter(
            (period) => period.start && period.end,
          );

          if (!ranges.length) {
            weekdaysErrors.push(item.weekday);
          } else {
            ranges?.forEach((range) => {
              const rangeBody: {
                weekDay: WeekDays;
                startTime: string;
                endTime: string;
                id?: string;
              } = {
                weekDay: item.weekday,
                startTime: range.start,
                endTime: range.end,
              };

              if (!range.id.includes('range')) {
                rangeBody.id = range.id;
              }

              periods.push(rangeBody);
            });
          }
        }
      });

      body.periods = periods;
      body.activeWeekDays = activeWeekDays;
    }

    if (weekdaysErrors.length) {
      updateIsAvailable = false;
      setWeekdaysWithErrors(weekdaysErrors);
    } else {
      setWeekdaysWithErrors([]);
    }

    if (
      values.accountsType === 'SPECIFIC' &&
      !values.primaryAccountIds?.length
    ) {
      updateIsAvailable = false;

      setError('primaryAccountIds', {
        type: 'custom',
        message: 'Primary Accounts is required',
      });
    }

    if (
      values.createChildrenTransactions &&
      (!values.fromAccountId || !values.toAccountId || !values.rateSourceId)
    ) {
      updateIsAvailable = false;

      if (!values.toAccountId) {
        setError('toAccountId', {
          type: 'custom',
          message: 'To Account is required',
        });
      }

      if (!values.fromAccountId) {
        setError('fromAccountId', {
          type: 'custom',
          message: 'From Account is required',
        });
      }

      if (!values.rateSourceId) {
        setError('rateSourceId', {
          type: 'custom',
          message: 'Rate Source is required',
        });
      }
    }

    if (updateIsAvailable) {
      const response = (await update({
        id: settingId as string,
        body,
      })) as {
        error: ResponseError | { data: { message: string } };
        data?: { data: AutoExchangeFormValues };
      };

      if (response) {
        if (response?.error) {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError,
            setGlobalError,
          });
        } else {
          reset({
            ...fields,
            ...body,
            maxAmountPeriodInHours: values.maxAmountPeriodInHours,
          });

          setDefaultSchedule(schedule);
        }
      }
    }
  });

  const getDetailsAction = useCallback(async (id: string) => {
    const response = (await getDetails(id)) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList: setGlobalError,
      });
    }
  }, []);

  const handleClose = () => {
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);
    reset({});
    dispatch(clearAutoSettingsDetailsState());

    const url = generatePath(ROUTES.AUTO_SETTINGS_CURRENCY, {
      currency: currency as string,
    });

    setTimeout(() => {
      navigate(`${url}${search || ''}`);
    }, 100);
  };

  const onStatusChange = (active: boolean) => {
    setValue('active', active);

    changeStatus({
      id: settingId as string,
      active,
    });
  };

  useEffect(() => {
    if (
      activeTab === 'log' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  useEffect(() => {
    if (isError) {
      setValue('active', !status);
    }
  }, [isError, status]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (settingId) {
      getDetailsAction(settingId);
    }
  }, [settingId]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.ACCOUNTS_CHART) &&
      !accountCategories
    ) {
      getAccountsCategories('all=true&include=primaryAccounts');
    }
  }, [activePermissions, accountCategories]);

  useEffect(() => {
    if (accountCategories) {
      const primaryAccounts: PrimaryAccount[] = [];

      accountCategories?.forEach((item) => {
        item.accountGroups?.forEach((group) => {
          group?.primaryAccounts?.forEach((primary) => {
            if (!primaryAccounts.find((account) => account.id === primary.id)) {
              primaryAccounts.push(primary);
            }
          });
        });
      });

      setPrimaryAccountsList(primaryAccounts);
    }
  }, [accountCategories]);

  useEffect(() => {
    if (autoSettingsDetails) {
      if (autoSettingsDetails?.fromAccountId) {
        const currentAccount = fromAccounts?.find(
          (item) => item.value === autoSettingsDetails?.fromAccountId,
        );

        if (!currentAccount) {
          getFromAccountById(autoSettingsDetails?.fromAccountId);
        }
      }

      if (autoSettingsDetails?.toAccountId) {
        const currentAccount = toAccounts?.find(
          (item) => item.value === autoSettingsDetails?.toAccountId,
        );

        if (!currentAccount) {
          getToAccountById(autoSettingsDetails?.toAccountId);
        }
      }
    }
  }, [autoSettingsDetails]);

  useEffect(() => {
    if (autoSettingsDetails) {
      reset({
        active: autoSettingsDetails.active,
        exchangeCurrencyFromId: autoSettingsDetails.exchangeCurrencyFromId,
        exchangeCurrencyToId: autoSettingsDetails.exchangeCurrencyToId,
        maxAmountForPeriod: autoSettingsDetails.maxAmountForPeriod,
        maxAmountPerOperation: autoSettingsDetails.maxAmountPerOperation,
        minAmountPerOperation: autoSettingsDetails.minAmountPerOperation,
        maxAmountPeriodInHours: autoSettingsDetails.maxAmountPeriodInHours / 24,
        rateSourceLifetimeSeconds:
          autoSettingsDetails.rateSourceLifetimeSeconds,
        exchangeType:
          autoSettingsDetails.maxAmountCurrencyId ===
          autoSettingsDetails.exchangeCurrencyFromId
            ? 'SELL'
            : 'BUY',
        maxAmountCurrencyId: autoSettingsDetails.maxAmountCurrencyId,
        accountsType: autoSettingsDetails?.accountsType,
        fromAccountId: autoSettingsDetails?.fromAccountId || '',
        toAccountId: autoSettingsDetails?.toAccountId || '',
        rateSourceId: autoSettingsDetails?.rateSourceId || '',
        primaryAccountIds: autoSettingsDetails?.primaryAccountIds,
        createChildrenTransactions:
          autoSettingsDetails?.createChildrenTransactions,
        targetStatus: autoSettingsDetails?.targetStatus || '',
      });

      let nextSchedule = ([] as Schedule[]).concat(schedule);

      if (autoSettingsDetails?.primaryAccountIds) {
        setSelectedPrimaryAccounts(autoSettingsDetails?.primaryAccountIds);
      }

      if (autoSettingsDetails?.activeWeekDays?.length) {
        nextSchedule = nextSchedule?.map((item) => {
          const result = { ...item };

          if (
            autoSettingsDetails.activeWeekDays?.find(
              (weekday) => weekday === item.weekday,
            )
          ) {
            result.active = true;
          }

          return result;
        });

        if (autoSettingsDetails?.periods?.length) {
          nextSchedule = nextSchedule?.map((item) => {
            const result = { ...item };

            const currentWeekdayTimerange =
              autoSettingsDetails?.periods?.filter(
                (period) => period.weekDay === item.weekday,
              );

            let nextTimeRange = (
              [] as { id: string; start: string; end: string }[]
            ).concat(result.timerange);

            if (currentWeekdayTimerange?.length) {
              nextTimeRange = currentWeekdayTimerange?.map((period) => ({
                id: period.id as string,
                start: period.startTime,
                end: period.endTime,
              }));
            }

            result.timerange = nextTimeRange;

            return result;
          });
        }

        setSchedule(nextSchedule);
        setDefaultSchedule(nextSchedule);
      } else {
        setDefaultSchedule(schedule);
      }
    }
  }, [autoSettingsDetails]);

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        <div className={cn.settingsTabsWrapper}>
          <Tabs
            tabs={[
              {
                name: 'Details',
                value: 'details',
              },
              {
                name: 'Change log',
                value: 'log',
              },
            ]}
            activeTab={activeTab}
            onClick={(tab) => setActiveTab(tab as 'details' | 'log')}
          />
        </div>
        {activeTab === 'details' && (
          <AutoSettingsForm
            control={control}
            errors={errors}
            currencies={currencies}
            schedule={schedule}
            setSchedule={setSchedule}
            currencyFrom={currencyFrom}
            currencyTo={currencyTo}
            exchangeType={exchangeType}
            isEditable={Boolean(
              activePermissions?.includes(
                PermissionEnum.SETTINGS_AUTO_EXCHANGE_UPDATE_ACTION,
              ),
            )}
            onStatusChange={onStatusChange}
            changeStatusInProgress={changeStatusInProgress}
            weekdaysWithErrors={weekdaysWithErrors}
            accountCategories={accountCategories}
            accountsCategoriesLoading={accountsCategoriesLoading}
            accountsType={accountsType}
            selectedPrimaryAccounts={selectedPrimaryAccounts}
            setSelectedPrimaryAccounts={setSelectedPrimaryAccounts}
            primaryAccountsList={primaryAccountsList}
            setValue={setValue}
            createSyncedTransaction={createChildrenTransactions}
            fromAccounts={fromAccounts}
            fromAccountsListIsLoading={fromAccountsListIsLoading}
            fromAccountsNextPageIsLoading={fromAccountsNextPageIsLoading}
            onScrollFromAccounts={onScrollFromAccounts}
            fromAccountsPagination={fromAccountsPagination}
            toAccounts={toAccounts}
            toAccountsListIsLoading={toAccountsListIsLoading}
            toAccountsNextPageIsLoading={toAccountsNextPageIsLoading}
            onScrollToAccounts={onScrollToAccounts}
            toAccountsPagination={toAccountsPagination}
            onSearchAutocomplete={onSearchAutocomplete}
            rateSource={rateSource?.map((item) => ({
              name: item.name,
              value: item.id,
              description: item.description,
            }))}
            activeSettingsVisible
          />
        )}
        {activeTab === 'log' && (
          <>
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <ChangeLogs
                entityType="EXCHANGE_AUTO_RULE"
                entityId={settingId as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={changeLogs}
                activePermission={activePermissions}
                users={users}
                pagination={changeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getChangeLogsAction}
              />
            )}
            {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <PermissionDenied />
            )}
          </>
        )}
      </>
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        errorSidepanel={globalError}
        onClose={() => {
          if (formIsDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        sidepanelExpandedKey="auto-settings-panel"
        title={
          isLoading || isFetching
            ? 'Loading...'
            : `${
                currencies?.find(
                  (item) =>
                    item.id === autoSettingsDetails?.exchangeCurrencyFromId,
                )?.description
              } - ${
                currencies?.find(
                  (item) =>
                    item.id === autoSettingsDetails?.exchangeCurrencyToId,
                )?.description
              }`
        }
        className={cn.rateDetailsSidepanel}
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_AUTO_EXCHANGE_UPDATE_ACTION,
            ) && (
              <ButtonPrimary
                text={requestInProgress ? 'Loading...' : 'Save'}
                onClick={onSubmit}
                disabled={!formIsDirty || requestInProgress}
                bold
              />
            )}

            <ButtonSecondary
              text="Close"
              onClick={() => {
                if (formIsDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              className="button-white"
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default AutoSettingsItem;
